<template>
  <div class="page-wrapper">
    <!-- SUCCESS MODAL -->
    <div v-if="showSuccess" class="modal fade show d-block modal-overlay">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content text-center p-5">
          <i class="bi bi-check-circle-fill success-icon"></i>
          <h3>Verification Successful!</h3>
          <p class="text-muted">Your account has been verified successfully.</p>
          <button @click="goToLogin" class="btn btn-success w-100">
            Go to Login <i class="bi bi-arrow-right ms-2"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MAIN VERIFICATION CARD -->
    <div class="form-container">
      <div class="card shadow-lg border-0">
        <div class="card-body p-5">
          
          <!-- Header Icon -->
          <div class="text-center mb-4">
            <div class="verification-icon mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
            </div>
          </div>

          <h2 class="text-center mb-2">Verify Your Account</h2>
          <p class="text-center text-muted mb-4">
            Please enter the verification code sent to your registered email/phone
          </p>

          <form @submit.prevent="handleVerify">
            <!-- Verification Code Input -->
            <div class="mb-4">
              <label class="form-label text-center d-block fw-bold">Verification Code</label>
              <div class="code-inputs">
                <input 
                  v-for="(digit, index) in 6" 
                  :key="index"
                  type="text"
                  maxlength="1"
                  class="code-input"
                  v-model="verificationCode[index]"
                  @input="handleCodeInput(index, $event)"
                  @keyup="handleCodeKeyup(index, $event)"
                  @paste="handlePaste"
                  ref="codeInputs"
                  :disabled="verifying"
                />
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="alert alert-danger d-flex align-items-center" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{{ error }}</div>
            </div>

            <!-- Verify Button -->
            <button 
              type="submit" 
              class="btn btn-primary btn-lg w-100 mb-3"
              :disabled="verifying || !isCodeComplete"
            >
              <span v-if="verifying" class="spinner-border spinner-border-sm me-2"></span>
              {{ verifying ? 'Verifying...' : 'Verify Account' }}
            </button>

            <!-- Resend Code -->
            <div class="text-center">
              <button 
                type="button"
                class="btn btn-link text-decoration-none"
                @click="resendCode"
                :disabled="resendTimer > 0 || verifying"
              >
                <i class="bi bi-arrow-repeat me-1"></i>
                {{ resendTimer > 0 ? `Resend code in ${resendTimer}s` : "Resend Verification Code" }}
              </button>
            </div>

            <!-- Back to Login -->
            <div class="text-center mt-3">
              <small>
                <a href="#" @click.prevent="goToLogin" class="text-decoration-none">
                  <i class="bi bi-arrow-left me-1"></i> Back to Login
                </a>
              </small>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AccountVerification',
  data() {
    return {
      verificationCode: ['', '', '', '', '', ''],
      verifying: false,
      error: '',
      showSuccess: false,
      resendTimer: 0,
      API_BASE_URL: 'https://golang.roberms.com/api'
    }
  },
  computed: {
    isCodeComplete() {
      return this.verificationCode.every(digit => digit && digit.length === 1);
    },
    fullVerificationCode() {
      return this.verificationCode.join("");
    }
  },
  mounted() {
    // Auto-focus first input
    if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
      setTimeout(() => {
        this.$refs.codeInputs[0].focus();
      }, 100);
    }
  },
  methods: {
    handleCodeInput(index, event) {
      const value = event.target.value;
      if (value && /^\d$/.test(value)) {
        this.verificationCode[index] = value;
        if (index < 5) {
          this.$refs.codeInputs[index + 1].focus();
        }
      } else if (value === "") {
        this.verificationCode[index] = "";
      } else {
        this.verificationCode[index] = "";
      }
      
      // Clear error when user starts typing
      if (this.error) this.error = '';
    },

    handleCodeKeyup(index, event) {
      if (event.key === 'Backspace' && index > 0 && !this.verificationCode[index]) {
        this.$refs.codeInputs[index - 1].focus();
      }
    },

    handlePaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text');
      const numbers = pastedData.replace(/\D/g, '').slice(0, 6);
      
      if (numbers) {
        const digits = numbers.split('');
        for (let i = 0; i < digits.length && i < 6; i++) {
          this.verificationCode[i] = digits[i];
        }
        
        // Focus on the next empty input or last input
        const nextEmptyIndex = this.verificationCode.findIndex(digit => !digit);
        if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
          this.$refs.codeInputs[nextEmptyIndex].focus();
        } else {
          this.$refs.codeInputs[5].focus();
        }
      }
    },

    async handleVerify() {
      if (!this.isCodeComplete) {
        this.error = 'Please enter the complete verification code';
        return;
      }

      this.verifying = true;
      this.error = '';

      try {
        const response = await fetch(`${this.API_BASE_URL}/verify/account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            verification_code: this.fullVerificationCode
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Verification failed');
        }

        // Show success modal
        this.showSuccess = true;
        
        // Store token if returned
        if (data.data && data.data.token) {
          localStorage.setItem('token', data.data.token);
          localStorage.setItem('user', JSON.stringify(data.data.user));
        }
        
      } catch (err) {
        this.error = err.message;
        // Clear verification code on error
        this.verificationCode = ['', '', '', '', '', ''];
        if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
          this.$refs.codeInputs[0].focus();
        }
      } finally {
        this.verifying = false;
      }
    },

    async resendCode() {
      if (this.resendTimer > 0) return;
      
      this.error = '';

      try {
        const response = await fetch(`${this.API_BASE_URL}/resend-verification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Failed to resend code');
        }

        // Reset code inputs
        this.verificationCode = ['', '', '', '', '', ''];
        if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
          this.$refs.codeInputs[0].focus();
        }
        
        this.startResendTimer();
        
        // Show success message
        alert('Verification code has been resent successfully!');
        
      } catch (err) {
        this.error = err.message;
      }
    },

    startResendTimer() {
      this.resendTimer = 60;
      const interval = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer--;
        } else {
          clearInterval(interval);
        }
      }, 1000);
    },

    goToLogin() {
      // Clear any stored data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/login');
    }
  },

}
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background: var(--dash-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.form-container {
  width: 100%;
  max-width: 550px;
}

.card {
  border-radius: var(--dash-radius-lg);
  border: none;
}

.card-body {
  border-radius: var(--dash-radius-lg);
}

.verification-icon {
  width: 80px;
  height: 80px;
  background: var(--dash-primary-soft);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: var(--dash-primary-ink);
}

.verification-icon svg {
  color: var(--dash-primary-ink);
}

.code-inputs {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin: 20px 0;
}

.code-input {
  width: 60px;
  height: 70px;
  text-align: center;
  font-size: 28px;
  font-weight: 600;
  border: 2px solid var(--dash-line);
  border-radius: var(--dash-radius);
  transition: all 0.3s ease;
  background: white;
}

.code-input:focus {
  outline: none;
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  transform: scale(1.05);
}

.code-input:disabled {
  background-color: var(--dash-bg-alt);
  cursor: not-allowed;
}

.btn-primary {
  background: var(--dash-primary);
  border: none;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-link {
  color: var(--dash-primary);
  font-weight: 500;
}

.btn-link:hover:not(:disabled) {
  color: var(--dash-primary-hover);
}

.btn-link:disabled {
  color: var(--dash-faint);
  cursor: not-allowed;
}

.success-icon {
  font-size: 2.25rem;
  color: var(--dash-success);
  margin-bottom: 20px;
}

.modal-overlay {
  background: rgba(0, 0, 0, 0.7);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.modal-dialog {
  max-width: 450px;
  width: 90%;
  margin: auto;
}

.modal-content {
  border-radius: var(--dash-radius-lg);
  border: none;
}

.form-label {
  font-weight: 500;
  color: var(--dash-text);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .code-input {
    width: 45px;
    height: 55px;
    font-size: 24px;
  }
  
  .code-inputs {
    gap: 8px;
  }
  
  .card-body {
    padding: 1.5rem !important;
  }
  
  .form-container {
    max-width: 100%;
  }
}

/* Animation for success modal */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-content {
  animation: fadeIn 0.3s ease-out;
}
</style>